/**
 * Schreibaufträge auf `sige_arbeitsschritte` je Arbeitsschritt — strikt
 * nacheinander (Codex-Review Runde 3, 13.10.2026). Vorbild:
 * `schriftWarteschlange.ts`.
 *
 * Denselben Schritt schreiben mehrere Stellen: das Autospeichern und das
 * manuelle Speichern der Detailansicht, „Unterlagen ergänzen“, der
 * Bearbeiten-Dialog und das Löschen in der Liste. Ohne Ordnung überholen
 * sich ihre Schreibvorgänge auf dem Netz, und der zuletzt ankommende
 * überschreibt den anderen mit einer veralteten Liste.
 *
 * Regeln:
 *  - Je Arbeitsschritt-id EINE Kette; Aufträge derselben id laufen strikt
 *    nacheinander, Aufträge verschiedener ids unabhängig voneinander.
 *  - Ein Auftrag ist eine Funktion, die ihre Payload ERST BEI AUSFÜHRUNG
 *    berechnet (in der Regel: frische Zeile lesen, zusammenführen,
 *    schreiben). Was vor ihm in der Kette lief, ist dann vollständig
 *    geschrieben.
 *  - Ein Fehler bricht die Kette nicht: der nächste Auftrag läuft trotzdem.
 *    Das Promise des fehlgeschlagenen Auftrags wird abgelehnt, damit sein
 *    Aufrufer den Fehler melden kann.
 *  - Modulweit (eine Instanz), damit Detailansicht und Liste dieselbe
 *    Warteschlange benutzen, ohne dass der Editor sie durchreichen muss.
 */

const ketten = new Map<string, Promise<void>>();

/**
 * Reiht einen Auftrag für einen Arbeitsschritt ein. Das Promise erfüllt sich
 * mit dem Ergebnis des Auftrags bzw. wird mit seinem Fehler abgelehnt, sobald
 * er (nach allen früheren Aufträgen derselben id) gelaufen ist.
 */
export const reiheSchrittAuftrag = <T>(schrittId: string, auftrag: () => Promise<T> | T): Promise<T> => {
  const vorher = ketten.get(schrittId) ?? Promise.resolve();
  const ergebnis = vorher.then(() => auftrag());
  // Die Kette selbst lehnt nie ab — sonst liefe der nächste Auftrag nicht.
  const kette = ergebnis.then(() => undefined, () => undefined);
  ketten.set(schrittId, kette);
  void kette.then(() => {
    if (ketten.get(schrittId) === kette) ketten.delete(schrittId);
  });
  return ergebnis;
};

/** Erfüllt sich, wenn alle bis jetzt eingereihten Aufträge der id gelaufen sind. */
export const schrittWarteschlangeLeer = (schrittId: string): Promise<void> =>
  ketten.get(schrittId) ?? Promise.resolve();
